import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { AccountTreeOutlined, AddOutlined, ApartmentOutlined, ArrowBackOutlined, ChevronRightOutlined, EditOutlined, PersonOutlined, TaskAltOutlined } from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import {
  FeedbackSnackbar,
  CommonFilterBar,
  CompactDateField,
  DetailDrawerBody,
  DetailSection,
  DialogSurface,
  FormAutocomplete,
  FormDialog,
  FormTextField,
  InlineNotice,
  PageScaffold,
  ResourceCollection,
  ResourceState,
  SearchField,
  StatusBadge,
  ValueBadge,
  designTokens,
  type ResourceCollectionState,
  type StatusTone,
} from '../../../design-system';
import { OrganizationChart } from '../OrganizationChart';
import { OrganizationPlanningWorkspace } from '../OrganizationPlanningWorkspace';
import { useOrganizationMemberPreviews } from '../useOrganizationMemberPreviews';
import { getFixtureStaffingPlan, type StaffingOperationsPort, type StaffingPlan } from '../../staffing/public';
import { useAsyncResource } from '../../../shared/useAsyncResource';
import { fixtureOrganizations, fixtureOrganizationWorkers } from '../fixtures';
import type { OrganizationChange, OrganizationNode } from '../model';
import type { OrganizationPort, OrganizationStudioRoutePort } from '../port';

const emptyStudioRoute: OrganizationStudioRoutePort = { read: () => undefined, open: () => undefined, replace: () => undefined, close: () => undefined, subscribe: () => () => undefined };

type OrganizationChangeRow = {
  id: string;
  operation: string;
  organization: string;
  effectiveDate: string;
  status: string;
  source: OrganizationChange;
};

const fixtureOrganizationChanges: OrganizationChange[] = [
  { id: 'org-change-1', descriptor: 'Platform Engineering Team 조직장 변경', operation: 'CHANGE', organization: { id: 'platform', descriptor: 'Platform Engineering Team' }, proposed: { manager: { id: 'worker-03', descriptor: '이도현' } }, effectiveDate: '2026-09-01', status: 'IN_PROGRESS', businessProcess: { id: 'bp-org-1', descriptor: '조직 변경' }, createdAt: '2026-08-14T05:00:00Z', submittedAt: '2026-08-14T05:05:00Z' },
  { id: 'org-change-2', descriptor: 'Business Division 조직명 변경', operation: 'CHANGE', organization: { id: 'business', descriptor: 'Business Division' }, proposed: { name: 'Growth Division' }, effectiveDate: '2026-10-01', status: 'DRAFT', businessProcess: { id: 'bp-org-2', descriptor: '조직 변경' }, createdAt: '2026-08-15T01:00:00Z' },
];

function organizationChangeStatusLabel(status: string) {
  return ({ DRAFT: '초안', IN_PROGRESS: '승인 중', COMPLETED: '완료', DENIED: '반려', CANCELLED: '취소' } as Record<string, string>)[status] || status;
}
function organizationChangeStatusTone(status: string): StatusTone {
  if (status === 'COMPLETED') return 'success';
  if (status === 'IN_PROGRESS') return 'info';
  if (status === 'DRAFT') return 'warning';
  return 'neutral';
}

export type OrganizationPageProps = {
  port: OrganizationPort;
  fixtureMode: boolean;
  canWrite: boolean;
  canCancelHistory: boolean;
  planningPort?: StaffingOperationsPort;
  onOpenPlan?: (planId: string) => void;
  onOpenPlans?: () => void;
  studioRoute?: OrganizationStudioRoutePort;
};

export function OrganizationPage({
  port,
  fixtureMode,
  canWrite,
  canCancelHistory,
  planningPort,
  onOpenPlan,
  onOpenPlans,
  studioRoute,
}: OrganizationPageProps) {
  const [asOf, setAsOf] = useState(() => studioRoute?.readInitialContext?.()?.effectiveDate ?? new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul' }).format(new Date()));
  const [planning, setPlanning] = useState<{ sessionId: string; planId: string; initialPlan?: StaffingPlan; workerOrganizationId?: string; workerId?: string }>();
  const planningRef = useRef(planning);
  planningRef.current = planning;
  const route = studioRoute ?? emptyStudioRoute;
  const studioId = useSyncExternalStore(route.subscribe, route.read, () => undefined);
  const editorView = useSyncExternalStore(route.subscribe, route.readView ?? (() => 'studio' as const), () => 'studio' as const);
  const initialContext = useSyncExternalStore(route.subscribe, route.readInitialContext ?? (() => undefined), () => undefined);
  const pendingWorkerOrganization = useRef<string | undefined>(undefined);
  const [studioLoading, setStudioLoading] = useState(false);
  const [studioError, setStudioError] = useState('');
  const [studioRetry, setStudioRetry] = useState(0);
  const closePlanning = () => { setPlanning(undefined); setStudioError(''); studioRoute?.close(); };
  const beginPlanning = (workerOrganizationId?: string) => {
    if (!canWrite || !planningPort) return;
    pendingWorkerOrganization.current = workerOrganizationId;
    if (studioRoute) studioRoute.open('new');
    else setPlanning({ sessionId: crypto.randomUUID(), planId: 'new', workerOrganizationId });
  };
  useEffect(() => {
    if (!studioRoute) return;
    if (!studioId) { setPlanning(undefined); setStudioLoading(false); setStudioError(''); return; }
    if (!canWrite || !planningPort) { setPlanning(undefined); setStudioLoading(false); setStudioError('조직 변경안을 조회하고 수정할 권한이 없습니다.'); return; }
    if (planningRef.current?.planId === studioId) return;
    if (studioId === 'new') {
      if (initialContext?.effectiveDate) setAsOf(initialContext.effectiveDate);
      setPlanning({ sessionId: crypto.randomUUID(), planId: 'new', workerOrganizationId: initialContext?.organizationId ?? pendingWorkerOrganization.current, workerId: initialContext?.workerId });
      pendingWorkerOrganization.current = undefined;
      setStudioError(''); setStudioLoading(false);
      return;
    }
    let active = true;
    setPlanning(undefined); setStudioLoading(true); setStudioError('');
    void (fixtureMode ? Promise.resolve(getFixtureStaffingPlan(studioId)) : planningPort.getPlan(studioId)).then((plan) => {
      if (!active) return;
      if (!plan) throw new Error('초안을 찾을 수 없습니다.');
      if (plan.status !== 'DRAFT') { onOpenPlan?.(plan.id); return; }
      setAsOf(plan.effectiveDate);
      setPlanning({ sessionId: crypto.randomUUID(), planId: plan.id, initialPlan: plan });
    }).catch((cause: unknown) => { if (active) setStudioError(cause instanceof Error ? cause.message : '초안을 불러오지 못했습니다.'); }).finally(() => { if (active) setStudioLoading(false); });
    return () => { active = false; };
  }, [canWrite, fixtureMode, planningPort, studioId, studioRetry, studioRoute, initialContext]);
  const chartRegion = useRef<HTMLDivElement>(null);
  const [mutationMode, setMutationMode] = useState<'CREATE' | 'CHANGE' | 'END'>();
  const resource = useAsyncResource(
    () => fixtureMode ? Promise.resolve(fixtureOrganizations) : port.listOrganizations(asOf),
    [asOf, port, fixtureMode],
  );
  const workersResource = useAsyncResource(
    () => canWrite && mutationMode && mutationMode !== 'END' ? (fixtureMode ? Promise.resolve(fixtureOrganizationWorkers) : port.listWorkers()) : Promise.resolve([]),
    [canWrite, port, fixtureMode, mutationMode],
  );
  const history = useAsyncResource(
    () => fixtureMode ? Promise.resolve(fixtureOrganizationChanges) : port.listChanges(),
    [port, fixtureMode],
  );
  const [selectedID, setSelectedID] = useState<string>();
  const [detailOpen, setDetailOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [navigationNotice, setNavigationNotice] = useState<string>();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyQuery, setHistoryQuery] = useState('');
  const [historyStatus, setHistoryStatus] = useState('');
  const [historyItems, setHistoryItems] = useState<OrganizationChange[]>([]);
  const [selectedHistory, setSelectedHistory] = useState<OrganizationChange>();
  const historyRowTrigger = useRef<HTMLElement | null>(null);
  const historyBackButton = useRef<HTMLButtonElement>(null);
  const [historyActionNotice, setHistoryActionNotice] = useState<string>();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState<string>();
  const [cancelAttempt, setCancelAttempt] = useState<{ key: string; comment: string }>();
  const [organizationName, setOrganizationName] = useState('');
  const [parentID, setParentID] = useState('');
  const [managerID, setManagerID] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(() => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul' }).format(new Date()));
  const [reason, setReason] = useState('');
  const [mutationLoading, setMutationLoading] = useState(false);
  const [mutationError, setMutationError] = useState<string>();
  const [pageNotice, setPageNotice] = useState<string>();
  const [draftKey, setDraftKey] = useState('');
  const [submitKey, setSubmitKey] = useState('');
  useEffect(() => {
    if (!historyOpen || cancelOpen) return;
    const frame = window.requestAnimationFrame(() => {
      const target = selectedHistory ? historyBackButton.current : historyRowTrigger.current;
      if (target?.isConnected) target.focus();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [historyOpen, selectedHistory?.id, cancelOpen]);
  useEffect(() => {
    if (!history.data) return;
    setHistoryItems(history.data);
    setSelectedHistory((current) => current ? history.data?.find((change) => change.id === current.id) ?? current : undefined);
  }, [history.data]);
  const all = resource.data ?? [];
  const memberPreviews = useOrganizationMemberPreviews(port, all, asOf, true, fixtureMode);
  const rootOrganization = all.find((node) => !node.parentId) ?? all[0];
  const selected = all.find((node) => node.id === selectedID);
  const organizationChildren = new Map<string, OrganizationNode[]>();
  for (const node of all) {
    if (!node.parentId) continue;
    const siblings = organizationChildren.get(node.parentId) ?? [];
    siblings.push(node);
    organizationChildren.set(node.parentId, siblings);
  }
  const selectedChildren = selected ? organizationChildren.get(selected.id) ?? [] : [];
  const selectedDescendantIDs = new Set<string>();
  if (selected) {
    const pending = [selected.id];
    while (pending.length) {
      const parent = pending.shift();
      for (const child of parent ? organizationChildren.get(parent) ?? [] : []) {
        if (selectedDescendantIDs.has(child.id)) continue;
        selectedDescendantIDs.add(child.id);
        pending.push(child.id);
      }
    }
  }
  const parentOptions = [
    ...(mutationMode === 'CHANGE' ? [{ id: '__UNCHANGED__', label: '변경하지 않음' }] : []),
    ...all.filter((organization) => mutationMode !== 'CHANGE' || (organization.id !== selected?.id && !selectedDescendantIDs.has(organization.id)))
      .map((organization) => ({ id: organization.id, label: organization.name })),
  ];
  const managerOptions = [
    ...(mutationMode === 'CHANGE'
      ? [{ id: '__UNCHANGED__', label: '변경하지 않음' }, { id: '__CLEAR__', label: '조직장 해제' }]
      : [{ id: '', label: '미지정' }]),
    ...(workersResource.data ?? []).map((worker) => ({ id: worker.id, label: `${worker.name} · ${worker.workerNumber}` })),
  ];
  const locations = [...new Set(all.map((node) => node.location))].sort((left, right) => left.localeCompare(right, 'ko-KR'));
  const matchingOrganizations = all.filter((node) =>
    `${node.name} ${node.subtitle} ${node.manager}`.toLocaleLowerCase('ko-KR').includes(query.trim().toLocaleLowerCase('ko-KR')) &&
    (!locationFilter || node.location === locationFilter)
  );
  const matchingOrganizationCount = matchingOrganizations.length;
  const byId = new Map(all.map((node) => [node.id, node]));
  const filteredVisibleIds = new Set<string>();
  for (const node of matchingOrganizations) {
    let current: OrganizationNode | undefined = node;
    const visited = new Set<string>();
    while (current && !visited.has(current.id)) {
      visited.add(current.id);
      filteredVisibleIds.add(current.id);
      current = current.parentId ? byId.get(current.parentId) : undefined;
    }
  }
  const historyRows: OrganizationChangeRow[] = historyItems.filter((change) =>
    `${change.descriptor} ${change.organization?.descriptor || ''}`.toLowerCase().includes(historyQuery.toLowerCase()) &&
    (!historyStatus || change.status === historyStatus)
  ).map((change) => ({ id: change.id, operation: change.operation === 'CREATE' ? '조직 생성' : change.proposed.end ? '조직 폐지' : '조직 변경', organization: change.organization?.descriptor || change.proposed.name || change.proposed.code || '신규 조직', effectiveDate: change.effectiveDate, status: change.status, source: change }));
  const revealOrganization = (id: string) => {
    if ((query.trim() || locationFilter) && !filteredVisibleIds.has(id)) {
      setQuery('');
      setLocationFilter('');
      setNavigationNotice('선택한 조직을 표시하기 위해 검색어와 근무지 필터를 해제했습니다.');
    }
    setSelectedID(id);
  };
  const selectOrganization = (id: string) => { revealOrganization(id); setDetailOpen(true); };
  const closeOrganizationDetail = () => {
    setDetailOpen(false);
    if (selectedID) {
      revealOrganization(selectedID);
      requestAnimationFrame(() => chartRegion.current?.querySelector<HTMLElement>(`[data-testid="hierarchy-node-${CSS.escape(selectedID)}"]`)?.focus({ preventScroll: true }));
    }
  };
  const resetMutation = (mode: 'CREATE' | 'CHANGE' | 'END') => {
    if (!canWrite) return;
    setMutationMode(mode);
    setOrganizationName(mode === 'CHANGE' && selected ? selected.name : '');
    setParentID(mode === 'CREATE' ? (selected?.id || rootOrganization?.id || '') : '__UNCHANGED__');
    setManagerID(mode === 'CREATE' ? '' : '__UNCHANGED__');
    setEffectiveDate(new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul' }).format(new Date()));
    setReason('');
    setMutationError(undefined);
    setDraftKey(crypto.randomUUID());
    setSubmitKey(crypto.randomUUID());
  };
  const submitMutation = () => {
    if (!canWrite) return;
    if (!mutationMode || !effectiveDate || (mutationMode !== 'CREATE' && !reason.trim())) {
      setMutationError('필수 항목을 입력해 주세요.');
      return;
    }
    if (mutationMode === 'CREATE' && (!organizationName.trim() || (all.length > 0 && !parentID))) {
      setMutationError(all.length > 0 ? '조직명과 상위 조직을 입력해 주세요.' : '조직명을 입력해 주세요.');
      return;
    }
    if (mutationMode === 'CHANGE' && selected && organizationName.trim() === selected.name && parentID === '__UNCHANGED__' && managerID === '__UNCHANGED__') {
      setMutationError('변경할 조직명, 상위 조직 또는 조직장을 선택해 주세요.');
      return;
    }
    setMutationLoading(true);
    setMutationError(undefined);
    const createDraft = mutationMode === 'CREATE'
      ? (fixtureMode ? Promise.resolve({ ...fixtureOrganizationChanges[0], id: 'fixture-create', status: 'DRAFT' }) : port.createOrganization({ name: organizationName.trim(), ...(parentID ? { parent: { id: parentID } } : {}), effectiveDate, ...(managerID ? { manager: { id: managerID } } : {}), ...(reason.trim() ? { reason: reason.trim() } : {}) }, draftKey))
      : (fixtureMode ? Promise.resolve({ ...fixtureOrganizationChanges[0], id: 'fixture-change', status: 'DRAFT' }) : port.createChange(selected!.id, mutationMode === 'END' ? { end: true, effectiveDate, reason: reason.trim() } : { ...(organizationName.trim() !== selected!.name ? { name: organizationName.trim() } : {}), ...(parentID !== '__UNCHANGED__' ? { parent: { id: parentID } } : {}), ...(managerID !== '__UNCHANGED__' ? { manager: managerID === '__CLEAR__' ? null : { id: managerID } } : {}), effectiveDate, reason: reason.trim() }, draftKey));
    void createDraft.then((draft) => fixtureMode ? Promise.resolve(draft) : port.submitChange(draft.id, submitKey)).then((change) => {
      const label = mutationMode === 'CREATE' ? '조직 생성' : mutationMode === 'END' ? '조직 폐지' : '조직 변경';
      setPageNotice(`${label}을 승인 요청했습니다. 결재 완료 후 ${change.effectiveDate} 기준 조직도에 반영됩니다.`);
      setMutationMode(undefined);
      setDetailOpen(false);
      history.reload();
    }, (error: unknown) => setMutationError(error instanceof Error ? error.message : '조직 변경을 승인 요청하지 못했습니다.')).finally(() => setMutationLoading(false));
  };
  const openCancelHistory = () => {
    if (!canCancelHistory || !selectedHistory || !['DRAFT', 'IN_PROGRESS'].includes(selectedHistory.status)) return;
    setCancelReason('');
    setCancelError(undefined);
    setCancelAttempt(undefined);
    setHistoryActionNotice(undefined);
    setCancelOpen(true);
  };
  const closeHistory = () => {
    if (cancelLoading) return;
    setHistoryOpen(false);
    setSelectedHistory(undefined);
    setCancelOpen(false);
    setCancelError(undefined);
    setCancelAttempt(undefined);
    setHistoryActionNotice(undefined);
  };
  const backFromHistoryCancellation = () => {
    if (cancelLoading) return;
    setCancelOpen(false);
    setCancelError(undefined);
    setCancelAttempt(undefined);
  };
  const finishCancelHistory = (target: OrganizationChange, cancelled: OrganizationChange) => {
    setHistoryItems((current) => current.map((change) => change.id === cancelled.id ? cancelled : change));
    setSelectedHistory(cancelled);
    setCancelOpen(false);
    setCancelReason('');
    setCancelAttempt(undefined);
    setHistoryActionNotice(`${target.descriptor} 요청을 취소했습니다.`);
    setPageNotice(`${target.descriptor} 요청을 취소했습니다.`);
  };
  const cancelHistoryChange = async () => {
    const target = selectedHistory;
    const comment = cancelAttempt?.comment ?? cancelReason.trim();
    if (cancelLoading || !canCancelHistory || !target || !['DRAFT', 'IN_PROGRESS'].includes(target.status)) return;
    if (!comment) {
      setCancelError('취소 사유를 입력해 주세요.');
      return;
    }
    const attempt = cancelAttempt ?? { key: crypto.randomUUID(), comment };
    if (!cancelAttempt) setCancelAttempt(attempt);
    setCancelLoading(true);
    setCancelError(undefined);
    try {
      const event = fixtureMode ? undefined : await port.cancelEvent(target.businessProcess.id, attempt.comment, attempt.key);
      const cancelled: OrganizationChange = {
        ...target,
        status: 'CANCELLED',
        completedAt: event?.cancelledAt ?? event?.completedAt ?? new Date().toISOString(),
      };
      finishCancelHistory(target, cancelled);
      if (!fixtureMode) history.reload();
    } catch (error) {
      if (!fixtureMode) {
        try {
          const refreshed = await port.listChanges();
          const current = refreshed.find((change) => change.id === target.id);
          if (current?.status === 'CANCELLED') {
            finishCancelHistory(target, current);
            return;
          }
        } catch {
          // 원래 mutation 오류와 안정 키를 유지해 같은 요청을 재시도한다.
        }
      }
      setCancelError(error instanceof Error ? error.message : '조직 변경을 취소하지 못했습니다.');
    } finally {
      setCancelLoading(false);
    }
  };
  const historyColumns: GridColDef<OrganizationChangeRow>[] = [
    { field: 'operation', headerName: '변경', width: 110, renderCell: ({ value }) => <ValueBadge label={value} /> },
    { field: 'organization', headerName: '조직', minWidth: 200, flex: 1 },
    { field: 'effectiveDate', headerName: '시행일', width: 110 },
    { field: 'status', headerName: '상태', width: 105, renderCell: ({ value }) => <StatusBadge tone={organizationChangeStatusTone(String(value))} label={organizationChangeStatusLabel(String(value))} /> },
  ];
  const historyState: ResourceCollectionState = history.loading
    ? { kind: 'loading', label: '조직 변경 이력을 불러오는 중입니다.' }
    : history.error
      ? { kind: 'error', message: history.error.message, onRetry: history.reload }
      : historyItems.length === 0
        ? { kind: 'empty', title: '조직 변경 이력이 없습니다.' }
        : { kind: 'ready' };
  const futureChanges = historyItems.filter((change) => change.effectiveDate > asOf && !['DENIED', 'CANCELLED'].includes(change.status));
  const futureChangesByOrganization = new Map<string, number>();
  for (const change of futureChanges) {
    if (!change.organization?.id) continue;
    futureChangesByOrganization.set(change.organization.id, (futureChangesByOrganization.get(change.organization.id) ?? 0) + 1);
  }
  const selectedFutureChanges = selected ? futureChanges.filter((change) => change.organization?.id === selected.id) : [];
  const organizationDetailActions = selected && canWrite ? <Stack direction="row" spacing={1} useFlexGap sx={{ justifyContent: 'flex-end', flexWrap: 'wrap' }}>
    {planningPort && <Button variant="outlined" onClick={() => { setDetailOpen(false); beginPlanning(selected.id); }}>구성원 이동</Button>}
    <Button variant="outlined" color="error" disabled={!selected.parentId} onClick={() => resetMutation('END')}>폐지 예약</Button><Button variant="contained" startIcon={<EditOutlined />} onClick={() => resetMutation('CHANGE')}>조직 수정</Button></Stack> : undefined;
  const organizationDetailContent = selected && (
        <DetailDrawerBody padding="none">
          <Box sx={{ px: `${designTokens.layout.detailInlineInset}px`, py: 2, bgcolor: designTokens.color.actionSoft, borderBottom: 1, borderColor: designTokens.color.divider }}><Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}><Avatar variant="rounded" sx={{ bgcolor: designTokens.color.action, color: designTokens.color.canvas }}><ApartmentOutlined /></Avatar><Box sx={{ flex: 1, minWidth: 0 }}><Typography variant="h5" sx={{ overflowWrap: 'anywhere' }}>{selected.name}</Typography><Typography variant="body2" color="text.secondary">{selected.subtitle}</Typography></Box></Stack><Stack direction="row" spacing={0.75} sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 0.75 }}><Chip variant="outlined" icon={<PersonOutlined />} label={`직접 소속 ${selected.count}명`} /><ValueBadge label={selected.location} />{selectedFutureChanges.length > 0 && <Chip variant="outlined" color="primary" icon={<TaskAltOutlined />} label={`향후 변경 ${selectedFutureChanges.length}건`} />}</Stack></Box>
          <DetailSection title="기준일 정보" rows={[["조직장", selected.manager], ['상위 조직', selected.parentId ? all.find((node) => node.id === selected.parentId)?.name || '상위 조직 정보 없음' : '루트 조직'], ['조직 코드·설명', selected.subtitle], ['기본 근무지', selected.location], ['직접 소속 구성원', `${selected.count}명`], ['조회 기준일', asOf.replaceAll('-', '.')]]} />
          <Divider /><Box sx={{ px: `${designTokens.layout.detailInlineInset}px`, py: 1.5 }}><Typography variant="body2" sx={{ fontWeight: 750, mb: 1 }}>향후 변경 안건</Typography>{selectedFutureChanges.map((change) => <Paper key={change.id} variant="outlined" sx={{ p: 1.25, mb: 0.75, bgcolor: designTokens.color.brandSky, borderColor: designTokens.color.brandBorder }}><Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}><Box sx={{ flex: 1 }}><Typography variant="body2" sx={{ fontWeight: 700 }}>{change.descriptor}</Typography><Typography variant="caption" color="text.secondary">{change.effectiveDate.replaceAll('-', '.')} 시행</Typography></Box><StatusBadge tone={organizationChangeStatusTone(change.status)} label={organizationChangeStatusLabel(change.status)} /></Stack></Paper>)}{!selectedFutureChanges.length && <Typography variant="body2" color="text.secondary">향후 변경 안건이 없습니다.</Typography>}</Box>
          <Divider /><Box sx={{ px: `${designTokens.layout.detailInlineInset}px`, py: 1.5 }}><Typography variant="body2" sx={{ fontWeight: 750, mb: 1 }}>직속 하위 조직 · {selectedChildren.length}개</Typography>{selectedChildren.length ? selectedChildren.sort((left, right) => left.name.localeCompare(right.name, 'ko-KR')).map((child) => <ListItemButton key={child.id} onClick={() => selectOrganization(child.id)} sx={{ px: 1 }}><ListItemIcon sx={{ minWidth: 30 }}><AccountTreeOutlined fontSize="small" /></ListItemIcon><ListItemText primary={child.name} secondary={`조직장 ${child.manager} · 직접 소속 ${child.count}명`} slotProps={{ primary: { sx: { overflowWrap: 'anywhere' } }, secondary: { sx: { overflowWrap: 'anywhere' } } }} /><ChevronRightOutlined fontSize="small" /></ListItemButton>) : <Typography variant="body2" color="text.secondary">직속 하위 조직이 없습니다.</Typography>}</Box>
        </DetailDrawerBody>
  );
  const organizationDetail = selected && <DialogSurface open={detailOpen} onClose={closeOrganizationDetail} closeLabel="조직 정보 닫기" title="조직 정보" size="large" actions={organizationDetailActions}>{organizationDetailContent}</DialogSurface>;
  if (studioId && (studioLoading || studioError)) return <ResourceState loading={studioLoading} error={studioError || undefined} onRetry={() => setStudioRetry(value => value + 1)} empty={false}
    errorAction={<Stack direction="row" spacing={1}><Button onClick={() => setStudioRetry(value => value + 1)}>다시 시도</Button><Button onClick={closePlanning}>조직도로 돌아가기</Button></Stack>}>{null}</ResourceState>;
  if (planning && canWrite && planningPort) return <OrganizationPlanningWorkspace key={planning.sessionId} organizations={all} effectiveDate={asOf} initialPlan={planning.initialPlan} initialSelectedId={selectedID} initialWorkerOrganizationId={planning.workerOrganizationId} initialWorkerId={planning.workerId} memberPreviews={memberPreviews} port={planningPort} fixtureMode={fixtureMode}
    editorView={studioRoute?.readView ? editorView : undefined} onViewChange={studioRoute?.setView}
    onPersisted={(plan) => {
      const current = planningRef.current;
      if (!current || current.planId === plan.id) return;
      const next = { ...current, planId: plan.id };
      planningRef.current = next; setPlanning(next); studioRoute?.replace(plan.id);
    }}
    onClose={closePlanning} onSaved={(plan, submitted) => {
      setPlanning(undefined); setPageNotice(plan.status === 'CANCELED' ? '발령안을 취소했습니다.' : submitted ? '조직 발령안을 승인 요청했습니다.' : '조직 발령안을 저장했습니다.');
      studioRoute?.close();
      onOpenPlan?.(plan.id);
    }} />;
  return <><PageScaffold
    title="조직도"
    width="fluid"
    bodyMode="fill"
    controls={<Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: `${designTokens.space.xs}px`, minWidth: 0, width: '100%' }}>
      <SearchField value={query} onValueChange={(value) => { setQuery(value); setNavigationNotice(undefined); }} label="조직 검색" placeholder="조직명·조직장 검색" disabled={!resource.data} sx={{ flex: { xs: '1 1 0', lg: `0 1 ${designTokens.layout.filterSearchWidth}px` }, width: 0, minWidth: 0 }} />
      <Box sx={{ flex: { xs: '1 1 100%', lg: '1 1 auto' }, minWidth: 0 }}>
        <CommonFilterBar key={asOf} searchable={false} filters={[{ label: '근무지', value: locationFilter, onChange: (value) => { setLocationFilter(value); setNavigationNotice(undefined); }, options: [{ label: '전체', value: '' }, ...locations.map((location) => ({ label: location, value: location }))] }]} trailing={<Typography variant="caption" color="text.secondary" role="status" aria-live="polite" sx={{ ml: 'auto' }}>검색 결과 {matchingOrganizationCount}개 조직</Typography>} />
      </Box>
    </Box>}
    utilities={<CompactDateField required label="기준일" value={asOf} onChange={(nextAsOf) => { if (!nextAsOf) return; setAsOf(nextAsOf); setSelectedID(undefined); setDetailOpen(false); setLocationFilter(''); setNavigationNotice(undefined); }} />}
    actions={<><Button variant="weak" onClick={() => setHistoryOpen(true)}>변경 이력{futureChanges.length ? ` · 향후 ${futureChanges.length}` : ''}</Button>{onOpenPlans && <Button variant="outlined" onClick={onOpenPlans}>발령안 목록</Button>}{canWrite && planningPort && <Button variant="outlined" startIcon={<AddOutlined />} onClick={() => resetMutation('CREATE')}>조직 추가</Button>}</>}
    primaryAction={canWrite ? planningPort
      ? <Button variant="fill" disabled={resource.loading || Boolean(resource.error)} onClick={() => { setDetailOpen(false); beginPlanning(); }}>조직 발령</Button>
      : <Button variant="fill" startIcon={<AddOutlined />} onClick={() => resetMutation('CREATE')}>조직 추가</Button> : undefined}
  >
    <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <FeedbackSnackbar open={Boolean(pageNotice || historyActionNotice)} message={pageNotice || historyActionNotice} tone="success" onClose={() => { setPageNotice(undefined); setHistoryActionNotice(undefined); }} />
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <ResourceState loading={resource.loading} error={resource.error?.message} empty={!all.length} onRetry={resource.reload} emptyTitle="기준일에 유효한 조직이 없습니다." emptyDescription="다른 기준일을 선택하거나 조직을 추가해 주세요.">
          <Stack spacing={`${designTokens.layout.pageSectionGap}px`} sx={{ height: '100%', minHeight: 0 }}>
            {navigationNotice && <InlineNotice severity="info" announcement="polite" density="compact" onClose={() => setNavigationNotice(undefined)}>{navigationNotice}</InlineNotice>}
            <Box ref={chartRegion} sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
              <OrganizationChart nodes={locationFilter ? all.filter(node => filteredVisibleIds.has(node.id)) : all} memberPreviews={memberPreviews} scheduledChanges={futureChangesByOrganization} selectedId={selectedID} onSelect={selectOrganization} query={query} filterActive={Boolean(locationFilter)} revealId={selectedID} fill defaultFit />
            </Box>
          </Stack>
        </ResourceState>
      </Box>
      {organizationDetail}
      <DialogSurface
        open={historyOpen}
        onClose={closeHistory}
        title={cancelOpen ? '조직 변경 취소' : selectedHistory ? '조직 변경 상세' : '조직 변경 이력'}
        closeLabel="조직 변경 이력 닫기"
        description={selectedHistory?.descriptor}
        busy={cancelLoading}
        onSubmit={cancelOpen ? cancelHistoryChange : undefined}
        size="large"
        mobilePresentation="fullscreen"
        contentMode="fill"
        actions={cancelOpen ? <>
          <Button variant="weak" disabled={cancelLoading} onClick={backFromHistoryCancellation}>돌아가기</Button>
          <Button type="submit" variant="fill" color="error" disabled={!cancelReason.trim() || cancelLoading}>{cancelLoading ? '취소 중' : '변경 취소'}</Button>
        </> : selectedHistory ? <>
          <Button ref={historyBackButton} variant="weak" startIcon={<ArrowBackOutlined />} onClick={() => { setSelectedHistory(undefined); setHistoryActionNotice(undefined); }}>변경 이력으로</Button>
          {canCancelHistory && ['DRAFT', 'IN_PROGRESS'].includes(selectedHistory.status) && <Button color="error" variant="outlined" onClick={openCancelHistory}>변경 취소</Button>}
        </> : <Button onClick={closeHistory}>닫기</Button>}
      >
        <Box sx={{ position: 'relative', minHeight: 0 }}>
        <Box aria-hidden={Boolean(selectedHistory)} inert={Boolean(selectedHistory)} sx={{ height: 500, width: '100%', minHeight: 0, position: selectedHistory ? 'absolute' : undefined, visibility: selectedHistory ? 'hidden' : undefined, pointerEvents: selectedHistory ? 'none' : undefined }}>
          <ResourceCollection state={historyState} rows={historyRows} totalCount={historyItems.length} columns={historyColumns} query={historyQuery} onQueryChange={setHistoryQuery} searchPlaceholder="조직명·변경 검색" filters={[{ label: '상태', value: historyStatus, onChange: setHistoryStatus, options: [{ label: '전체', value: '' }, { label: '초안', value: 'DRAFT' }, { label: '승인 중', value: 'IN_PROGRESS' }, { label: '완료', value: 'COMPLETED' }, { label: '반려', value: 'DENIED' }, { label: '취소', value: 'CANCELLED' }] }]} rowDisplayMode="all" onRowClick={({ row }) => { historyRowTrigger.current = document.activeElement instanceof HTMLElement ? document.activeElement : null; setHistoryActionNotice(undefined); setSelectedHistory(row.source); }} />
        </Box>
        {selectedHistory && !cancelOpen && <Box>
          <DetailSection title="변경 정보" rows={[["구분", <ValueBadge label={selectedHistory.operation === 'CREATE' ? '조직 생성' : selectedHistory.proposed.end ? '조직 폐지' : '조직 변경'} />], ["대상 조직", selectedHistory.organization?.descriptor || selectedHistory.proposed.name || '신규 조직'], ["시행일", selectedHistory.effectiveDate], ["상태", <StatusBadge label={organizationChangeStatusLabel(selectedHistory.status)} tone={organizationChangeStatusTone(selectedHistory.status)} />], ["조직 코드", selectedHistory.proposed.code || '-'], ["변경 조직명", selectedHistory.proposed.name || '-'], ["상위 조직", selectedHistory.proposed.parent?.descriptor || '변경 없음'], ["조직장", selectedHistory.proposed.clearManager ? '해제' : selectedHistory.proposed.manager?.descriptor || '변경 없음']]} />
        </Box>}
        {cancelOpen && selectedHistory && <Stack spacing={2}>
          <Alert severity="warning">진행 중인 변경 요청만 취소합니다. 조직과 기존 변경 이력은 삭제되지 않습니다.</Alert>
          <FormTextField required autoFocus label="취소 사유" value={cancelReason} characterCount={{ current: cancelReason.length, max: 4000 }} onChange={(event) => setCancelReason(event.target.value)} disabled={Boolean(cancelAttempt) || cancelLoading} helperText={cancelAttempt ? '같은 요청으로 안전하게 재시도합니다. 사유를 바꾸려면 돌아가기를 누른 뒤 다시 취소해 주세요.' : undefined} multiline minRows={3} slotProps={{ htmlInput: { maxLength: 4000 } }} />
          {cancelError && <Alert severity="error">{cancelError}</Alert>}
        </Stack>}
        </Box>
      </DialogSurface>
      <FormDialog
        open={Boolean(mutationMode)}
        onClose={() => setMutationMode(undefined)}
        onSubmit={submitMutation}
        title={mutationMode === 'CREATE' ? '조직 추가' : mutationMode === 'END' ? '조직 폐지 예약' : '조직 수정'}
        help="최종 승인 전에는 현재 조직도 원장이 변경되지 않습니다."
        cancelLabel="취소"
        submitLabel="승인 요청"
        busy={mutationLoading}
        busyLabel="제출 중"
      >
          {mutationError && <Alert severity="error">{mutationError}</Alert>}
          {(mutationMode === 'CREATE' || mutationMode === 'CHANGE') && <>
            <FormTextField required={mutationMode === 'CREATE'} label="조직명" value={organizationName} onChange={(event) => setOrganizationName(event.target.value)} />
            {mutationMode === 'CREATE' && !all.length ? <Alert severity="info">첫 조직은 상위 조직이 없는 루트 조직으로 생성됩니다.</Alert> : <FormAutocomplete
              options={parentOptions}
              value={parentOptions.find((option) => option.id === parentID) ?? null}
              getOptionLabel={(option) => option.label}
              getOptionKey={(option) => option.id}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, value) => setParentID(value?.id ?? (mutationMode === 'CHANGE' ? '__UNCHANGED__' : ''))}
              disabled={mutationMode === 'CHANGE' && !selected?.parentId}
              input={{ label: '상위 조직', required: mutationMode === 'CREATE', placeholder: '조직명 검색', helperText: mutationMode === 'CHANGE' && !selected?.parentId ? '루트 조직은 상위 조직을 변경할 수 없습니다.' : undefined }}
            />}
            <FormAutocomplete
              options={managerOptions}
              value={managerOptions.find((option) => option.id === managerID) ?? null}
              getOptionLabel={(option) => option.label}
              getOptionKey={(option) => option.id}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, value) => setManagerID(value?.id ?? (mutationMode === 'CHANGE' ? '__UNCHANGED__' : ''))}
              disabled={workersResource.loading}
              request={workersResource.error
                ? { status: 'error', message: '조직장 목록을 불러오지 못했습니다.', onRetry: workersResource.reload }
                : { status: workersResource.loading ? 'loading' : 'ready' }}
              input={{ label: '조직장', placeholder: '이름 또는 사번 검색' }}
            />
          </>}
          {mutationMode === 'END' && <Alert severity="warning">하위 조직이나 현재 주배치 구성원이 있으면 폐지할 수 없습니다. 기존 원장은 삭제되지 않습니다.</Alert>}
          <FormTextField required type="date" label="시행일" value={effectiveDate} onChange={(event) => setEffectiveDate(event.target.value)} />
          <FormTextField required={mutationMode !== 'CREATE'} label="변경 사유" value={reason} characterCount={{ current: reason.length, max: 4000 }} onChange={(event) => setReason(event.target.value)} multiline minRows={3} slotProps={{ htmlInput: { maxLength: 4000 } }} />
      </FormDialog>
    </Box>
  </PageScaffold>
  </>;
}
